import { OidcStandardClaims } from "oidc-client-ts";

export type OidcUsername = keyof Pick<
  OidcStandardClaims,
  "name" | "preferred_username" | "nickname"
>;

declare global {
  // ATTENTION: when adding a new environment variable, make sure to add it to the `generateEnvConfig` method below and
  // ensure it’s surfaced via Vite (see vite.config.ts `define`) or use import.meta.env if you switch to that pattern.
  interface Window {
    _env_: {
      DISABLE_TENANT_PREFIL_FROM_DOMAIN: string;
      API_URL: string;
      STELLAR_EXPERT_URL: string;
      HORIZON_URL: string;
      RPC_ENABLED: boolean;
      RECAPTCHA_SITE_KEY: string;
      SINGLE_TENANT_MODE: boolean;
      USE_SSO?: boolean;
      OIDC_AUTHORITY?: string;
      OIDC_CLIENT_ID?: string;
      OIDC_REDIRECT_URI?: string;
      OIDC_SCOPE?: string;
      OIDC_USERNAME_MAPPING?: OidcUsername;
      WALLETCONNECT_PROJECT_ID?: string;
      WALLETCONNECT_RELAY_URL?: string;
      WALLETCONNECT_LOG_LEVEL?: string;
      MULTISIG_FEATURE_FLAGS?: number;
    };
  }
}

const WINDOW_ENV_PATH = "/settings/env-config.js";

const parseMultisigFeatureFlagsEnv = (raw: unknown): number | undefined => {
  if (raw === undefined || raw === null || raw === "") return undefined;
  if (typeof raw === "number") {
    return Number.isInteger(raw) && raw >= 0 ? raw : undefined;
  }
  if (typeof raw === "string") {
    const parsed = Number.parseInt(raw, 10);
    return Number.isNaN(parsed) || parsed < 0 ? undefined : parsed;
  }
  return undefined;
};

const generateEnvConfig = async () => {
  if (process?.env?.REACT_APP_DISABLE_WINDOW_ENV !== "true") {
    const response = await fetch(WINDOW_ENV_PATH);
    const text = await response.text();

    const script = new Function(text);
    script.apply(null);
  }

  return {
    API_URL: process?.env?.REACT_APP_API_URL || window._env_.API_URL,
    DISABLE_TENANT_PREFIL_FROM_DOMAIN:
      process?.env?.REACT_APP_DISABLE_TENANT_PREFIL_FROM_DOMAIN ||
      window._env_.DISABLE_TENANT_PREFIL_FROM_DOMAIN,
    STELLAR_EXPERT_URL:
      process?.env?.REACT_APP_STELLAR_EXPERT_URL || window._env_.STELLAR_EXPERT_URL,
    HORIZON_URL: process?.env?.REACT_APP_HORIZON_URL || window._env_.HORIZON_URL,
    RPC_ENABLED: Boolean(process?.env?.REACT_APP_RPC_ENABLED || window._env_.RPC_ENABLED),
    RECAPTCHA_SITE_KEY:
      process?.env?.REACT_APP_RECAPTCHA_SITE_KEY || window._env_.RECAPTCHA_SITE_KEY,
    SINGLE_TENANT_MODE: Boolean(
      process?.env?.REACT_APP_SINGLE_TENANT_MODE || window?._env_?.SINGLE_TENANT_MODE,
    ),
    USE_SSO: Boolean(process?.env?.REACT_APP_USE_SSO || window?._env_?.USE_SSO),
    OIDC_AUTHORITY: process?.env?.REACT_APP_OIDC_AUTHORITY || window?._env_?.OIDC_AUTHORITY,
    OIDC_CLIENT_ID: process?.env?.REACT_APP_OIDC_CLIENT_ID || window?._env_?.OIDC_CLIENT_ID,
    OIDC_REDIRECT_URI:
      process?.env?.REACT_APP_OIDC_REDIRECT_URI || window?._env_?.OIDC_REDIRECT_URI,
    OIDC_SCOPE: process?.env?.REACT_APP_OIDC_SCOPE || window?._env_?.OIDC_SCOPE,
    OIDC_USERNAME_MAPPING:
      ((process?.env?.REACT_APP_OIDC_USERNAME_MAPPING ||
        window?._env_?.OIDC_USERNAME_MAPPING) as OidcUsername) || undefined,
    WALLETCONNECT_PROJECT_ID:
      process?.env?.REACT_APP_WALLETCONNECT_PROJECT_ID ||
      window?._env_?.WALLETCONNECT_PROJECT_ID ||
      "f54e2cf5d6e7a0f8ac954656ff5591b6",
    WALLETCONNECT_RELAY_URL:
      process?.env?.REACT_APP_WALLETCONNECT_RELAY_URL ||
      window?._env_?.WALLETCONNECT_RELAY_URL ||
      "wss://wallet-relay.core.dev.poweredbyvia.com",
    WALLETCONNECT_LOG_LEVEL:
      process?.env?.REACT_APP_WALLETCONNECT_LOG_LEVEL ||
      window?._env_?.WALLETCONNECT_LOG_LEVEL ||
      "error",
    MULTISIG_FEATURE_FLAGS:
      parseMultisigFeatureFlagsEnv(process?.env?.REACT_APP_MULTISIG_FEATURE_FLAGS) ??
      parseMultisigFeatureFlagsEnv(window?._env_?.MULTISIG_FEATURE_FLAGS) ??
      0,
  };
};

export const {
  DISABLE_TENANT_PREFIL_FROM_DOMAIN,
  API_URL,
  STELLAR_EXPERT_URL,
  HORIZON_URL,
  RPC_ENABLED,
  RECAPTCHA_SITE_KEY,
  SINGLE_TENANT_MODE,
  USE_SSO,
  OIDC_AUTHORITY,
  OIDC_CLIENT_ID,
  OIDC_REDIRECT_URI,
  OIDC_SCOPE,
  OIDC_USERNAME_MAPPING,
  WALLETCONNECT_PROJECT_ID,
  WALLETCONNECT_RELAY_URL,
  WALLETCONNECT_LOG_LEVEL,
  MULTISIG_FEATURE_FLAGS,
} = await generateEnvConfig();
